import React, { useState } from 'react';
import {
  Trash2, Clock, CheckCircle, XCircle, AlertTriangle, Download,
  FileText, User, Calendar, Mail, Search, Filter, ExternalLink
} from 'lucide-react';
import type { DeletionRequest, Jurisdiction } from '../../types/dataRetention';
import { StandardButton, StandardInput, StandardSelect, StandardEmptyState } from '../standard';

interface DeletionRequestManagerProps {
  userRole: 'system_admin' | 'compliance_officer' | 'viewer';
}

const DeletionRequestManager: React.FC<DeletionRequestManagerProps> = ({ userRole }) => {
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'pending' | 'approved' | 'in_progress' | 'completed' | 'rejected'>('all');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<Jurisdiction | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);

  // Mock deletion requests data
  const mockRequests: DeletionRequest[] = [
    {
      id: 'DR-001',
      userId: '12345',
      requestDate: new Date('2025-09-15'),
      requestedBy: 'john.doe@example.com',
      reason: 'User requested account closure and data deletion per CCPA',
      status: 'pending',
      jurisdiction: 'US',
      responseDueDate: new Date('2025-10-30'),
      daysToComplete: 15,
      priority: 'high'
    },
    {
      id: 'DR-002',
      userId: '12346',
      requestDate: new Date('2025-09-20'),
      requestedBy: 'jane.smith@example.com',
      reason: 'GDPR Article 17 - Right to be forgotten',
      status: 'approved',
      jurisdiction: 'UK',
      responseDueDate: new Date('2025-10-20'),
      daysToComplete: 5,
      approvedBy: 'admin@company.com',
      approvedDate: new Date('2025-09-21'),
      priority: 'urgent'
    },
    {
      id: 'DR-003',
      userId: '12347',
      requestDate: new Date('2025-09-10'),
      requestedBy: 'user@example.com',
      reason: 'PIPEDA compliance - User data deletion request',
      status: 'in_progress' as const,
      jurisdiction: 'CA',
      responseDueDate: new Date('2025-10-10'),
      daysToComplete: -5,
      approvedBy: 'admin@company.com',
      approvedDate: new Date('2025-09-11'),
      exportFilePath: '/exports/user-12347-2025-09-25.csv',
      priority: 'urgent'
    },
    {
      id: 'DR-004',
      userId: '12348',
      requestDate: new Date('2025-08-30'),
      requestedBy: 'test@example.com',
      reason: 'Account closure and data removal',
      status: 'completed',
      jurisdiction: 'US',
      responseDueDate: new Date('2025-10-14'),
      daysToComplete: 0,
      approvedBy: 'admin@company.com',
      approvedDate: new Date('2025-08-31'),
      completedDate: new Date('2025-09-15'),
      completedBy: 'data-admin@company.com',
      exportFilePath: '/exports/user-12348-2025-09-14.csv',
      priority: 'normal'
    },
    {
      id: 'DR-005',
      userId: '12349',
      requestDate: new Date('2025-09-25'),
      requestedBy: 'rejected@example.com',
      reason: 'User requested deletion but account has pending transactions',
      status: 'rejected',
      jurisdiction: 'UK',
      responseDueDate: new Date('2025-10-25'),
      daysToComplete: 20,
      rejectedBy: 'compliance@company.com',
      rejectedDate: new Date('2025-09-26'),
      rejectionReason: 'Active financial obligations pending. Cannot delete until transactions are settled.',
      priority: 'low'
    }
  ];

  // Filter requests
  const filteredRequests = mockRequests.filter(request => {
    if (selectedStatus !== 'all' && request.status !== selectedStatus) return false;
    if (selectedJurisdiction !== 'all' && request.jurisdiction !== selectedJurisdiction) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        request.id.toLowerCase().includes(query) ||
        request.userId.includes(query) ||
        request.requestedBy.toLowerCase().includes(query) ||
        request.reason.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Status badge colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'approved': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'in_progress': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 dark:text-red-400';
      case 'high': return 'text-orange-600 dark:text-orange-400';
      case 'normal': return 'text-blue-600 dark:text-blue-400';
      case 'low': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getJurisdictionBadge = (jurisdiction: Jurisdiction) => {
    const colors = {
      US: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      CA: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      QC: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      UK: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };
    return colors[jurisdiction];
  };

  const canModify = userRole === 'system_admin' || userRole === 'compliance_officer';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Trash2 className="w-6 h-6" />
            Deletion Request Manager
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage GDPR/CCPA/PIPEDA data deletion requests
          </p>
        </div>
        {canModify && (
          <StandardButton
            variant="primary"
            onClick={() => setShowNewRequestModal(true)}
            leftIcon={<Trash2 className="w-4 h-4" />}
          >
            New Deletion Request
          </StandardButton>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {mockRequests.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Approved</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {mockRequests.filter(r => r.status === 'approved').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Approved</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {mockRequests.filter(r => r.status === 'approved').length}
              </p>
            </div>
            <Download className="w-8 h-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {mockRequests.filter(r => r.status === 'completed').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Rejected</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {mockRequests.filter(r => r.status === 'rejected').length}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status filter */}
          <div>
            <StandardSelect
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as any)}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'rejected', label: 'Rejected' }
              ]}
            />
          </div>

          {/* Jurisdiction filter */}
          <div>
            <StandardSelect
              value={selectedJurisdiction}
              onChange={(e) => setSelectedJurisdiction(e.target.value as any)}
              options={[
                { value: 'all', label: 'All Jurisdictions' },
                { value: 'US', label: 'United States (CCPA)' },
                { value: 'CA', label: 'Canada (PIPEDA)' },
                { value: 'UK', label: 'United Kingdom (GDPR)' }
              ]}
            />
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Request ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Jurisdiction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Days Left
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {request.id}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <User className="w-3 h-3" />
                        {request.userId}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {request.requestedBy}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getJurisdictionBadge(request.jurisdiction)}`}>
                      {request.jurisdiction}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(request.status)}`}>
                      {request.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`flex items-center gap-1 text-sm font-medium ${getPriorityColor(request.priority || 'normal')}`}>
                      {request.priority === 'urgent' && <AlertTriangle className="w-4 h-4" />}
                      {request.priority?.toUpperCase() || 'NORMAL'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {request.responseDueDate?.toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${
                      (request.daysToComplete ?? 0) < 0 ? 'text-red-600 dark:text-red-400' :
                      (request.daysToComplete ?? 0) <= 7 ? 'text-orange-600 dark:text-orange-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>
                      {(request.daysToComplete ?? 0) < 0 ? `${Math.abs(request.daysToComplete ?? 0)} overdue` : `${request.daysToComplete ?? 0} days`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        title="View Details"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      {request.exportFilePath && (
                        <button
                          className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                          title="Download Export"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      {canModify && request.status === 'pending' && (
                        <>
                          <button
                            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                            title="Approve Request"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            title="Reject Request"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <StandardEmptyState
              title="No deletion requests found"
              message="No requests match your current filters"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletionRequestManager;
